import { REACT_APP__ENVIRONMENT } from "@smp/utils";
import { PropsWithChildren, useCallback, useState } from "react";

import { useContextLoader } from "@/context/loader";
import { useEpUsersMe } from "@/hooks/use_ep_users_me";

import { SupportMessenger } from "./components";
import { useCarrotQuest, useIntercom, useJivo } from "./hooks";
import { ContextSupportMessenger } from "./support_messenger.context";

export const ProviderSupportMessenger = ({
  children,
}: PropsWithChildren<{}>) => {
  const { me, isMeLoading } = useEpUsersMe();

  const {
    state: { config },
  } = useContextLoader();

  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const isElamaUser = me && me.billing_policy_id === "elama";

  let supportMessenger = config.support_messenger || "intercom";

  const {
    auth: carrotAuth,
    identify: carrotIdentify,
    isInited: isCarrotQuestInited,
    open: carrotOpen,
    startConversation: carrotStartConversation,
    track: carrotTrack,
  } = useCarrotQuest(
    supportMessenger === "carrotquest" &&
      REACT_APP__ENVIRONMENT !== "development",
  );

  const {
    show: intercomShow,
    showNewMessage: intercomShowNewMessage,
    update: intercomUpdate,
    isInited: isIntercomInited,
  } = useIntercom(
    supportMessenger === "intercom" && REACT_APP__ENVIRONMENT !== "development",
  );

  const {
    show: jivoShow,
    showNewMessage: jivoShowNewMessage,
    update: jivoUpdate,
    isInited: isJivoInited,
  } = useJivo(
    supportMessenger === "jivo" && REACT_APP__ENVIRONMENT !== "development",
  );

  const isInited =
    supportMessenger === "jivo"
      ? isJivoInited
      : supportMessenger === "carrotquest"
      ? isCarrotQuestInited
      : supportMessenger === "intercom"
      ? isIntercomInited
      : false;

  const open = (message?: string) => {
    if (supportMessenger === "carrotquest") {
      if (!message) {
        carrotOpen();
      } else {
        carrotStartConversation(message);
      }
    }

    if (supportMessenger === "intercom") {
      if (!message) {
        intercomShow();
      } else {
        intercomShowNewMessage(message);
      }
    }

    if (supportMessenger === "jivo") {
      if (!message) {
        jivoShow();
      } else {
        jivoShowNewMessage(message);
      }
    }
  };

  const supportMessangerUpdate = useCallback(
    (data: unknown) => {
      if (typeof data !== "object" || data === null) {
        return;
      }

      if (supportMessenger === "intercom") {
        delete (data as any).jivo_token;

        intercomUpdate(data);
      } else if (supportMessenger === "jivo") {
        delete (data as any).app_id;
        delete (data as any).user_hash;

        jivoUpdate(data);
      }
    },
    [intercomUpdate, jivoUpdate, supportMessenger],
  );

  return (
    <ContextSupportMessenger.Provider
      value={{
        actions: {
          carrotAuth,
          carrotIdentify,
          carrotTrack,
          disable: () => setIsDisabled(true),
          supportMessangerUpdate,
          open,
        },
        state: { isDisabled, isInited, service: supportMessenger },
      }}
    >
      {children}
      {!isMeLoading &&
        !isElamaUser &&
        (isInited || REACT_APP__ENVIRONMENT === "development") &&
        !isDisabled && <SupportMessenger />}
    </ContextSupportMessenger.Provider>
  );
};
